import React, { useEffect, useState, useRef, useCallback } from 'react';
import { 
    ConnectWebSocketForOverlay, 
    DisconnectWebSocket, 
    SetSocketMessageHandler, 
    ClearSocketMessageHandler 
} from '../../../utilities/BL/Zones';
import { FinishPlayback, GetState } from '../../../utilities/BL/Sequence';
import { 
    playPadTone, 
    playVictoryFanfare, 
    playErrorTone, 
    playCountdownBeep, 
    unlockAudioContext, 
    stopAllSounds 
} from '../../../utilities/Commons/SequenceAudioSynth';

/**
 * Paleta de colores estándar para los 6 botones/pads del juego Simón (Secuencia).
 * @constant {Array<string>}
 */
const CONFETTI_COLORS = ['#00ff41', '#ff003c', '#fffb00', '#00e1ff', '#b300ff', '#ff8800', '#ffffff'];

/**
 * Configuración predeterminada de botones si el backend no provee tema personalizado.
 * @constant {Array<{id: number, color: string, imageUrl: string, label: string}>}
 */
const DEFAULT_BUTTONS = [
    { id: 0, color: '#00ff41', imageUrl: '', label: 'VERDE' },
    { id: 1, color: '#ff003c', imageUrl: '', label: 'ROJO' },
    { id: 2, color: '#fffb00', imageUrl: '', label: 'AMARILLO' },
    { id: 3, color: '#00e1ff', imageUrl: '', label: 'AZUL' },
    { id: 4, color: '#b300ff', imageUrl: '', label: 'MORADO' },
    { id: 5, color: '#ff8800', imageUrl: '', label: 'NARANJA' }
];

/**
 * COMPONENTE PANTALLA GIGANTE: SECUENCIA (SIMON SAYS MULTIPLAYER)
 * Proyecta en la pantalla del estadio el tablero de 6 pads de neón interactivos,
 * el marco perimetral animado con efectos estroboscópicos, la secuencia rítmica sintetizada
 * con Web Audio API, el reflejo en vivo de toques del aficionado, y cinemáticas de victoria y error.
 * 
 * Implementa arquitectura Zero-Canvas con aceleración GPU CSS3 a 60 FPS y 0% CPU en reposo.
 * 
 * @component
 * @param {Object} props - Propiedades del componente.
 * @param {number|string} [props.screenId=1] - Identificador numérico de la pantalla.
 * @param {boolean} [props.isStandalone=true] - Si es true, administra su propia conexión WebSocket/SignalR.
 * @param {Object|null} [props.payload=null] - Mensaje o estado inyectado por el enrutador padre (Screen.js).
 * @param {string|null} [props.roomId=null] - Identificador de sala para aislamiento multi-tenant SaaS.
 * @param {Object|null} [props.currentGameState=null] - Estado alternativo inyectado directamente.
 * @returns {JSX.Element} Vista de pantalla gigante de Secuencia.
 */
const SequenceScreen = ({ 
    screenId = 1, 
    isStandalone = true, 
    payload = null, 
    roomId = null,
    currentGameState = null 
}) => {
    const [, setConnectionStatus] = useState('idle');
    
    const [gameState, setGameState] = useState({
        phase: 'WAITING',
        sequence: [],
        playerStep: 0,
        isPlaying: false,
        matchId: '',
        lastTappedColorId: null,
        buttons: DEFAULT_BUTTONS
    });

    const [activePad, setActivePad] = useState(null); // ID del pad iluminado actualmente
    const [countdownNumber, setCountdownNumber] = useState(null);
    const [confettiList, setConfettiList] = useState([]);
    
    const isPlayingRef = useRef(false);
    const lastTappedRef = useRef(null);
    const confettiTimerRef = useRef(null);
    const previousPhaseRef = useRef('WAITING');

    // Obtener roomId resuelto de props o de la URL
    const resolvedRoomId = roomId || (typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('roomId') : null);

    // Estilos CSS con aceleración GPU (Zero-Canvas)
    const customStyles = `
        :root {
            --pad0: #00ff41; /* Green */
            --pad1: #ff003c; /* Red */
            --pad2: #fffb00; /* Yellow */
            --pad3: #00e1ff; /* Blue */
            --pad4: #b300ff; /* Purple */
            --pad5: #ff8800; /* Orange */
            --bg-dark: #0a0a0f;
        }

        .sequence-screen-body {
            margin: 0;
            padding: 0;
            width: 100vw;
            height: 100vh;
            background-color: var(--bg-dark);
            display: flex;
            justify-content: center;
            align-items: center;
            overflow: hidden;
            font-family: 'Outfit', 'Inter', sans-serif;
            color: #fff;
            position: relative;
            user-select: none;
        }

        /* Neon dotted frame */
        .neon-frame {
            position: absolute;
            top: 20px;
            left: 20px;
            right: 20px;
            bottom: 20px;
            width: calc(100vw - 40px);
            height: calc(100vh - 40px);
            pointer-events: none;
            z-index: 10;
        }

        .neon-frame rect {
            width: calc(100% - 12px);
            height: calc(100% - 12px);
            x: 6px;
            y: 6px;
            fill: none;
            stroke: rgba(255, 255, 255, 0.05);
            stroke-width: 12px;
            stroke-dasharray: 0.1 35;
            stroke-linecap: round;
            transition: stroke 0.3s ease;
        }

        .neon-frame.active rect {
            animation: strobe-svg 0.5s infinite alternate;
        }

        .neon-frame.countdown rect {
            animation: countdown-pulse-svg 1s infinite alternate;
            stroke: rgba(255, 255, 255, 0.8);
        }

        .neon-frame.error rect {
            stroke: var(--pad1);
            filter: drop-shadow(0 0 20px var(--pad1));
            animation: slot-machine 1.5s linear infinite;
            stroke-width: 16px;
        }

        .neon-frame.win-crazy rect {
            animation: strobe-party 0.2s infinite alternate, slot-machine 0.5s linear infinite;
            stroke-width: 20px;
        }

        @keyframes slot-machine {
            to { stroke-dashoffset: -350; }
        }

        @keyframes strobe-party {
            0% { stroke: #00ff41; filter: drop-shadow(0 0 40px #00ff41); }
            33% { stroke: #00e1ff; filter: drop-shadow(0 0 40px #00e1ff); }
            66% { stroke: #b300ff; filter: drop-shadow(0 0 40px #b300ff); }
            100% { stroke: #fffb00; filter: drop-shadow(0 0 40px #fffb00); }
        }

        @keyframes strobe-svg {
            0% { stroke: rgba(255, 255, 255, 0.8); filter: drop-shadow(0 0 20px #fff); }
            100% { stroke: rgba(255, 255, 255, 0.2); }
        }

        @keyframes countdown-pulse-svg {
            0% { filter: drop-shadow(0 0 0px #fff); opacity: 0.3; }
            100% { filter: drop-shadow(0 0 35px #fff); opacity: 1; }
        }

        /* Grid */
        .grid-container {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            grid-gap: 40px;
            width: 900px;
            height: 550px;
            z-index: 20;
        }

        .pad {
            border-radius: 35px;
            background: rgba(20, 20, 25, 0.9);
            border: 8px solid;
            transition: transform 0.15s cubic-bezier(0.2, 0.9, 0.3, 1.2), box-shadow 0.15s ease, background-color 0.15s ease;
            position: relative;
            will-change: transform, box-shadow, background-color;
        }

        .pad-0 { border-color: rgba(0, 255, 65, 0.2); }
        .pad-1 { border-color: rgba(255, 0, 60, 0.2); }
        .pad-2 { border-color: rgba(255, 251, 0, 0.2); }
        .pad-3 { border-color: rgba(0, 225, 255, 0.2); }
        .pad-4 { border-color: rgba(179, 0, 255, 0.2); }
        .pad-5 { border-color: rgba(255, 136, 0, 0.2); }

        /* Active states */
        .pad-0.active { background-color: var(--pad0); box-shadow: 0 0 60px var(--pad0), inset 0 0 45px var(--pad0); border-color: #fff; transform: scale(1.06); }
        .pad-1.active { background-color: var(--pad1); box-shadow: 0 0 60px var(--pad1), inset 0 0 45px var(--pad1); border-color: #fff; transform: scale(1.06); }
        .pad-2.active { background-color: var(--pad2); box-shadow: 0 0 60px var(--pad2), inset 0 0 45px var(--pad2); border-color: #fff; transform: scale(1.06); }
        .pad-3.active { background-color: var(--pad3); box-shadow: 0 0 60px var(--pad3), inset 0 0 45px var(--pad3); border-color: #fff; transform: scale(1.06); }
        .pad-4.active { background-color: var(--pad4); box-shadow: 0 0 60px var(--pad4), inset 0 0 45px var(--pad4); border-color: #fff; transform: scale(1.06); }
        .pad-5.active { background-color: var(--pad5); box-shadow: 0 0 60px var(--pad5), inset 0 0 45px var(--pad5); border-color: #fff; transform: scale(1.06); }

        /* Overlays */
        .overlay-msg {
            position: absolute;
            font-size: 8rem;
            font-weight: 900;
            text-transform: uppercase;
            text-shadow: 0 0 30px rgba(0,0,0,0.9);
            z-index: 100;
            pointer-events: none;
            opacity: 0;
            transform: scale(0.5);
            transition: opacity 0.15s ease, transform 0.15s ease;
            will-change: transform, opacity;
        }

        .overlay-msg.show {
            opacity: 1;
            transform: scale(1);
            animation: pop-in 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
        }

        .overlay-msg.countdown-text.show {
            font-size: 20rem;
            animation: countdown-pop 1s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
        }

        .color-win {
            color: var(--pad0);
            text-shadow: 0 0 40px var(--pad0), 0 0 100px #fff;
            animation: win-vibrate 0.25s infinite;
        }

        .color-lose {
            color: var(--pad1);
            text-shadow: 0 0 40px var(--pad1), 0 0 100px #fff;
            animation: lose-glitch 0.35s infinite;
        }

        .win-body-vibration {
            animation: body-vibrate 0.1s infinite;
        }

        @keyframes body-vibrate {
            0% { transform: translate3d(3px, 3px, 0); }
            25% { transform: translate3d(-3px, -3px, 0); }
            50% { transform: translate3d(3px, -3px, 0); }
            75% { transform: translate3d(-3px, 3px, 0); }
            100% { transform: translate3d(0, 0, 0); }
        }

        @keyframes win-vibrate {
            0% { transform: scale(1.1) rotate(0deg); text-shadow: 0 0 50px var(--pad0), 0 0 100px #fff; }
            50% { transform: scale(1.1) rotate(1deg); text-shadow: 0 0 60px var(--pad3), 0 0 120px #fff; }
            100% { transform: scale(1.1) rotate(-1deg); text-shadow: 0 0 50px var(--pad0), 0 0 100px #fff; }
        }

        @keyframes lose-glitch {
            0% { transform: scale(1) translate3d(-10px, 0, 0) skewX(5deg); }
            50% { transform: scale(1.1) translate3d(10px, 0, 0) skewX(-5deg); color: #fff; }
            100% { transform: scale(1) translate3d(-5px, 0, 0) skewX(2deg); }
        }

        @keyframes pop-in {
            0% { transform: scale(0.5); opacity: 0; }
            100% { transform: scale(1); opacity: 1; }
        }

        @keyframes countdown-pop {
            0% { transform: scale(0.6); opacity: 0; filter: blur(10px); }
            50% { transform: scale(1.1); opacity: 1; filter: blur(0); }
            100% { transform: scale(1.4); opacity: 0; filter: blur(10px); }
        }

        /* CSS 3D Ephemeral Confetti Pieces (Zero-Canvas Hardware GPU) */
        .confetti-container {
            position: absolute;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            pointer-events: none;
            z-index: 5;
            overflow: hidden;
        }

        .confetti-piece {
            position: absolute;
            width: var(--size);
            height: calc(var(--size) * 0.45);
            background-color: var(--color);
            top: -20px;
            left: var(--left);
            opacity: 0;
            transform-style: preserve-3d;
            animation: fall-confetti var(--duration) linear var(--delay) forwards;
            will-change: transform, opacity;
        }

        @keyframes fall-confetti {
            0% {
                transform: translate3d(0, 0, 0) rotate3d(1, 1, 1, 0deg);
                opacity: 0;
            }
            10% {
                opacity: 1;
            }
            90% {
                opacity: 1;
            }
            100% {
                transform: translate3d(var(--drift), 105vh, 0) rotate3d(1.5, 2, 1, var(--rotation));
                opacity: 0;
            }
        }
    `;

    /**
     * Lanza el destello visual y el tono armónico de un pad.
     * 
     * @param {number} colorId - Índice del pad (0 a 5).
     * @param {number} [duration=200] - Duración del destello en ms.
     * @param {boolean} [playAudio=true] - Si reproduce el tono con Web Audio API.
     */
    const triggerFlash = useCallback((colorId, duration = 200, playAudio = true) => {
        setActivePad(colorId);
        if (playAudio && colorId !== null && colorId !== undefined) {
            playPadTone(colorId, Math.max(duration, 150));
        }
        setTimeout(() => {
            setActivePad(prev => (prev === colorId ? null : prev));
        }, duration);
    }, []);

    /**
     * Ciclo sincronizado de reproducción de la secuencia con conteo 3, 2, 1 y destellos rítmicos.
     * 
     * @param {Array<number>} sequence - Arreglo de índices de colores (ej. [0, 2, 4, 1]).
     * @param {string} matchId - ID de la partida.
     * @param {number} speed - Intervalo de reproducción en milisegundos.
     */
    const playSequence = useCallback(async (sequence, matchId, speed) => {
        if (isPlayingRef.current) return;
        isPlayingRef.current = true;

        // Desbloquear contexto de audio
        unlockAudioContext().catch(() => {});

        // 1. Conteo 3, 2, 1 con bips de audio
        for (let i = 3; i >= 1; i--) {
            setCountdownNumber(i);
            playCountdownBeep(false);
            await new Promise(r => setTimeout(r, 1000));
        }
        setCountdownNumber(null);
        playCountdownBeep(true);

        // Gap de espera antes del Simon
        await new Promise(r => setTimeout(r, 500));

        // 2. Destellos del Simon con tonos armónicos Web Audio
        const flashDuration = speed - 150 > 200 ? speed - 150 : 250;
        for (let i = 0; i < sequence.length; i++) {
            triggerFlash(sequence[i], flashDuration, true);
            await new Promise(r => setTimeout(r, speed));
        }

        isPlayingRef.current = false;

        // 3. Notificar fin de reproducción al backend
        await FinishPlayback(screenId, matchId, resolvedRoomId);
    }, [screenId, resolvedRoomId, triggerFlash]);

    /**
     * Genera 35 piezas de confeti CSS 3D efímero con eliminación limpia del DOM tras 3 segundos garantizados.
     */
    const generateConfetti = useCallback(() => {
        // Limpiar timer previo si existiera
        if (confettiTimerRef.current) {
            clearTimeout(confettiTimerRef.current);
            confettiTimerRef.current = null;
        }

        const list = [];
        for (let i = 0; i < 35; i++) {
            const startLeft = Math.random() * 100;
            const size = Math.random() * 12 + 10; // 10px a 22px
            const duration = Math.random() * 0.8 + 2.0; // 2.0s a 2.8s para entrar en los 3s
            const delay = Math.random() * 0.4; // 0s a 0.4s
            const drift = Math.random() * 20 - 10; // -10vw a 10vw
            const rotation = Math.random() * 720 + 360; // 360deg a 1080deg
            const color = CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)];

            list.push({
                id: i,
                style: {
                    '--left': `${startLeft}vw`,
                    '--size': `${size}px`,
                    '--color': color,
                    '--duration': `${duration}s`,
                    '--delay': `${delay}s`,
                    '--drift': `${drift}vw`,
                    '--rotation': `${rotation}deg`
                }
            });
        }
        setConfettiList(list);

        // Garantía estricta de 3 segundos: Eliminación limpia del DOM
        confettiTimerRef.current = setTimeout(() => {
            setConfettiList([]);
            confettiTimerRef.current = null;
        }, 3000);
    }, []);

    /**
     * Procesa los mensajes entrantes de SignalR.
     */
    const handleIncomingMessage = useCallback((message) => {
        if (!message || !message.type) return;

        const type = String(message.type).toLowerCase();
        const payloadData = message.payload !== undefined ? message.payload : message.data || {};

        if (type === 'sequence.layout') {
            // Iniciar ciclo de reproducción
            if (payloadData.sequence && payloadData.sequence.length > 0) {
                setGameState(prev => ({
                    ...prev,
                    ...payloadData,
                    buttons: (payloadData.buttons && payloadData.buttons.length > 0) ? payloadData.buttons : prev.buttons
                }));
                playSequence(payloadData.sequence, payloadData.matchId, payloadData.playbackSpeedMs || 500);
            }
        } else if (type === 'sequence.update') {
            // Actualizar estado general
            setGameState(prev => ({
                ...prev,
                ...payloadData,
                buttons: (payloadData.buttons && payloadData.buttons.length > 0) ? payloadData.buttons : prev.buttons
            }));
        } else if (type === 'sequence.reset') {
            // Reiniciar
            setGameState({
                phase: 'WAITING',
                sequence: [],
                playerStep: 0,
                isPlaying: false,
                matchId: '',
                lastTappedColorId: null,
                buttons: DEFAULT_BUTTONS
            });
            setActivePad(null);
            setCountdownNumber(null);
            isPlayingRef.current = false;
            stopAllSounds();
            if (confettiTimerRef.current) {
                clearTimeout(confettiTimerRef.current);
                confettiTimerRef.current = null;
            }
            setConfettiList([]);
        }
    }, [playSequence]);

    // Carga de estado inicial por HTTP
    const fetchInitialState = useCallback(async () => {
        const res = await GetState(screenId, resolvedRoomId);
        if (res && res.success && res.data) {
            // Si ya está en PLAYBACK, no interrumpimos
            if (res.data.phase !== 'PLAYBACK') {
                setGameState(prev => ({
                    ...prev,
                    ...res.data,
                    buttons: (res.data.buttons && res.data.buttons.length > 0) ? res.data.buttons : prev.buttons
                }));
            }
        }
    }, [screenId, resolvedRoomId]);

    // Conexión WebSocket / SignalR en modo Standalone
    useEffect(() => {
        if (!isStandalone) {
            return;
        }

        const connectWS = async () => {
            const overlayType = `sequence:screen:${screenId}`;
            await ConnectWebSocketForOverlay(
                overlayType,
                (status) => setConnectionStatus(status),
                (toastMsg) => console.log(`[SequenceScreen WS] ${toastMsg}`)
            );
            SetSocketMessageHandler(handleIncomingMessage);
        };

        connectWS();
        fetchInitialState();

        return () => {
            ClearSocketMessageHandler();
            DisconnectWebSocket();
            if (confettiTimerRef.current) {
                clearTimeout(confettiTimerRef.current);
            }
            stopAllSounds();
        };
    }, [screenId, isStandalone, handleIncomingMessage, fetchInitialState]);

    // Procesar payload cuando se integra desde Screen.js
    useEffect(() => {
        if (!isStandalone && payload) {
            handleIncomingMessage(payload);
        }
    }, [payload, isStandalone, handleIncomingMessage]);

    // Procesar currentGameState alternativo
    useEffect(() => {
        if (currentGameState) {
            setGameState(prev => ({
                ...prev,
                ...currentGameState,
                buttons: (currentGameState.buttons && currentGameState.buttons.length > 0) ? currentGameState.buttons : prev.buttons
            }));
        }
    }, [currentGameState]);

    // Reaccionar a toques del jugador y transiciones de fase (Audio + Cinemáticas)
    useEffect(() => {
        const currentPhase = gameState.phase;
        const prevPhase = previousPhaseRef.current;
        previousPhaseRef.current = currentPhase;

        // 1. Reflejo en vivo de toques en PLAYER_INPUT
        if (currentPhase === 'PLAYER_INPUT' && gameState.lastTappedColorId !== null && gameState.lastTappedColorId !== undefined) {
            const tapKey = `${gameState.matchId}-${gameState.playerStep}-${gameState.lastTappedColorId}`;
            if (lastTappedRef.current !== tapKey) {
                lastTappedRef.current = tapKey;
                // Destello y tono del botón presionado en vivo
                triggerFlash(gameState.lastTappedColorId, 220, true);
            }
        }

        // 2. Cinemática de Victoria (WON)
        if (currentPhase === 'WON') {
            if (prevPhase !== 'WON') {
                playVictoryFanfare();
                generateConfetti();
            }
        } else if (currentPhase === 'LOST') {
            // 3. Cinemática de Derrota (LOST)
            if (prevPhase !== 'LOST') {
                playErrorTone();
                if (confettiTimerRef.current) {
                    clearTimeout(confettiTimerRef.current);
                    confettiTimerRef.current = null;
                }
                setConfettiList([]);
            }
        } else {
            // Fases neutrales: limpiar confeti
            if (confettiTimerRef.current) {
                clearTimeout(confettiTimerRef.current);
                confettiTimerRef.current = null;
            }
            setConfettiList([]);
        }
    }, [
        gameState.phase, 
        gameState.lastTappedColorId, 
        gameState.playerStep, 
        gameState.matchId, 
        triggerFlash, 
        generateConfetti
    ]);

    // Resuelve la URL absoluta de los medios
    const getMediaUrl = (path) => {
        if (!path) return '';
        if (path.startsWith('http://') || path.startsWith('https://')) return path;
        const apiBase = (process.env.REACT_APP_API_URL || '').replace('/api/', '/').replace('/api', '/');
        return `${apiBase}${path}`;
    };

    const isWon = gameState.phase === 'WON';
    const isLost = gameState.phase === 'LOST';
    const isPlayback = gameState.phase === 'PLAYBACK';

    const buttonsToUse = (gameState.buttons && gameState.buttons.length > 0) ? gameState.buttons : DEFAULT_BUTTONS;

    const gridStyle = {
        display: 'grid',
        gridTemplateColumns: buttonsToUse.length <= 4 ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
        gridGap: '40px',
        width: '900px',
        height: '550px',
        zIndex: 20
    };

    return (
        <>
            <div className={`sequence-screen-body ${isWon ? 'win-body-vibration' : ''}`}>
                <style>{customStyles}</style>

                {/* Logo oficial del patrocinador */}
                <img 
                    src="/img/page/xolos-five.png" 
                    alt="Sponsor Logo"
                    style={{ 
                        position: 'fixed', 
                        top: '20px', 
                        left: '50%', 
                        transform: 'translateX(-50%)', 
                        width: '450px',
                        zIndex: 30 
                    }} 
                    onError={(e) => { 
                        e.target.onerror = null; 
                        e.target.src = '/img/page/xolos-five.png'; 
                    }}
                />

                {/* Marco de Neón SVG con animación punteada y estroboscópica */}
                <svg 
                    className={`neon-frame ${
                        isPlayback ? 'active' :
                        countdownNumber ? 'countdown' :
                        isWon ? 'win-crazy' :
                        isLost ? 'error' : ''
                    }`}
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <rect x="0" y="0" rx="30" ry="30"></rect>
                </svg>

                {/* Tablero de Pads / Botones Simón */}
                <div style={gridStyle}>
                    {buttonsToUse.map((btn) => {
                        const isActive = activePad === btn.id;
                        const padStyle = {
                            borderRadius: '35px',
                            background: isActive ? btn.color : 'rgba(20, 20, 25, 0.9)',
                            border: '8px solid',
                            borderColor: isActive ? '#fff' : `${btn.color}33`,
                            boxShadow: isActive ? `0 0 60px ${btn.color}, inset 0 0 45px ${btn.color}` : 'none',
                            transform: isActive ? 'scale(1.06)' : 'none',
                            transition: 'transform 0.15s cubic-bezier(0.2, 0.9, 0.3, 1.2), box-shadow 0.15s ease, background-color 0.15s ease',
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            willChange: 'transform, box-shadow, background-color'
                        };
                        return (
                            <div 
                                key={btn.id} 
                                style={padStyle}
                            >
                                {btn.imageUrl && (
                                    <img 
                                        src={getMediaUrl(btn.imageUrl)} 
                                        alt={btn.label} 
                                        style={{ 
                                            width: '110%', 
                                            height: '120%', 
                                            objectFit: 'cover',
                                            filter: isActive ? 'brightness(1.2)' : 'none',
                                            opacity: isActive ? 1 : 0.6,
                                            transition: 'filter 0.15s ease, opacity 0.15s ease'
                                        }} 
                                    />
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Conteo Regresivo Overlay */}
                {countdownNumber && (
                    <div className="overlay-msg countdown-text show">
                        {countdownNumber}
                    </div>
                )}

                {/* Cinemática Overlay: ¡GANASTE! */}
                {isWon && (
                    <div className="overlay-msg show color-win">
                        ¡GANASTE!
                    </div>
                )}

                {/* Cinemática Overlay: INCORRECTO */}
                {isLost && (
                    <div className="overlay-msg show color-lose">
                        INCORRECTO
                    </div>
                )}

                {/* Piezas de Confeti CSS 3D Efímero (3 segundos garantizados) */}
                {isWon && confettiList.length > 0 && (
                    <div className="confetti-container">
                        {confettiList.map(item => (
                            <div 
                                key={item.id} 
                                className="confetti-piece" 
                                style={item.style}
                            />
                        ))}
                    </div>
                )}

            </div>

            {/* Créditos de pie de página */}
            <p 
                className='FooterCredits' 
                style={{ 
                    position: 'fixed', 
                    bottom: '0', 
                    left: '50%', 
                    transform: 'translateX(-50%)', 
                    fontSize: '30px', 
                    color: '#fff',
                    zIndex: 30 
                }}
            >
                Hecho con ♥ por Interaktia | 2026
            </p>
        </>
    );
};

export { SequenceScreen };
export default SequenceScreen;
